import { SkillLevelBar } from '@/components/learning/skill-level-bar'
import { skillLevelValue } from '@/constants/learning'
import type { SkillLevel } from '@/types'
import { useMemo } from 'react'
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from 'recharts'

interface SkillRadarChartProps {
  currentSkills: Record<string, SkillLevel>
  targetSkills?: Record<string, SkillLevel> | null | undefined
}

const MAX_CHART_SKILLS = 8
const MIN_CHART_SKILLS = 3

export function SkillRadarChart({ currentSkills, targetSkills }: SkillRadarChartProps) {
  const allSkills = Object.entries(currentSkills)

  const { chartData, overflowSkills } = useMemo(() => {
    // Sort by level descending to show top skills on chart
    const sorted = [...allSkills].sort((a, b) => skillLevelValue[b[1]] - skillLevelValue[a[1]])

    const forChart = sorted.slice(0, MAX_CHART_SKILLS)
    const overflow = sorted.slice(MAX_CHART_SKILLS)

    const data = forChart.map(([name, level]) => ({
      skill: name,
      current: skillLevelValue[level],
      target: targetSkills?.[name] ? skillLevelValue[targetSkills[name]] : undefined,
    }))

    return { chartData: data, overflowSkills: overflow }
  }, [allSkills, targetSkills])

  // Fallback to bar list for fewer than 3 skills
  if (allSkills.length < MIN_CHART_SKILLS) {
    return (
      <div className="space-y-4">
        {allSkills.map(([name, level]) => (
          <div key={name}>
            <p className="text-sm font-medium capitalize mb-1">{name}</p>
            <SkillLevelBar currentLevel={level} targetLevel={targetSkills?.[name]} />
          </div>
        ))}
      </div>
    )
  }

  const hasTargets = chartData.some((d) => d.target !== undefined)

  return (
    <div>
      <ResponsiveContainer width="100%" height={320}>
        <RadarChart data={chartData} cx="50%" cy="50%" outerRadius="70%">
          <PolarGrid stroke="hsl(var(--border))" />
          <PolarAngleAxis
            dataKey="skill"
            tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
            className="capitalize"
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 4]}
            tickCount={5}
            tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
          />
          {hasTargets && (
            <Radar
              name="Target"
              dataKey="target"
              stroke="hsl(var(--primary) / 0.4)"
              fill="hsl(var(--primary) / 0.05)"
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />
          )}
          <Radar
            name="Current"
            dataKey="current"
            stroke="hsl(var(--primary))"
            fill="hsl(var(--primary) / 0.2)"
            strokeWidth={2}
          />
        </RadarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-2">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 bg-primary rounded" />
          <span className="text-xs text-muted-foreground">Current</span>
        </div>
        {hasTargets && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-primary/40 rounded border-dashed border border-primary/40" />
            <span className="text-xs text-muted-foreground">Target</span>
          </div>
        )}
      </div>

      {/* Overflow skills shown as bars */}
      {overflowSkills.length > 0 && (
        <div className="mt-6 space-y-3">
          <p className="text-xs text-muted-foreground font-medium">Other Skills</p>
          {overflowSkills.map(([name, level]) => (
            <div key={name}>
              <p className="text-sm font-medium capitalize mb-1">{name}</p>
              <SkillLevelBar currentLevel={level} targetLevel={targetSkills?.[name]} size="sm" />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
